import {
    ActionType,
    BotType,
    LocationOptionDTO,
    MatchField,
    MatchType,
    SubtractionType,
    UpdateConditionGroupRequest,
    UpdateConditionRequest,
    UpdateModifierRulesRequest,
    UpdateRuleSectionRequest,
    VehicleDataProvider
} from "../../../api/generated";
import parsePhoneNumberFromString, {CountryCode} from 'libphonenumber-js';
import {
    getAssistantLocationsByVehicleDataProvider,
    isAssistantPipelineSearchProvider
} from "../../../utils/assistantVehicleDataProvider";

export interface SubtractionRequest {
    subtractionType: SubtractionType;
    subtractionValue: number;
}

export interface RetailPriceRulesRequest {
    max?: number;
    subtraction?: SubtractionRequest;
}

export interface ConditionMultipliersRequest {
    excellentSubtraction?: SubtractionRequest;
    goodSubtraction?: SubtractionRequest;
    averageSubtraction?: SubtractionRequest;
    belowAverageSubtraction?: SubtractionRequest;
    poorSubtraction?: SubtractionRequest;
}

export interface ServiceHistoryMultipliersRequest {
    fullHistorySubtraction?: SubtractionRequest;
    partHistorySubtraction?: SubtractionRequest;
    noHistorySubtraction?: SubtractionRequest;
}

export type ValidatorOutcome = { valid: false; message?: string; } | { valid: true }

export type ValueValidator<T> = (fieldName: string, value?: T) => ValidatorOutcome | Promise<ValidatorOutcome>;

interface PatternValidationRule {
    pattern: RegExp;
    example: string;
}

const POSTCODE_VALIDATION_RULES: PatternValidationRule[] = [
    {
        pattern: /^([A-Za-z][A-Ha-hJ-Yj-y]?[0-9][A-Za-z0-9]? ?[0-9][A-Za-z]{2}|[Gg][Ii][Rr] ?0[Aa]{2})$/,
        example: "AB12 3CD"
    },
    {
        pattern: /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i,
        example: "K1A 0B1"
    },
    {
        pattern: /^(?:[AC-FHKNPRTV-Y]\d{2}|D6W)[ -]?[0-9AC-FHKNPRTV-Y]{4}$/i,
        example: "D02 AF30"
    },
    {
        pattern: /^\d{5}(?:-\d{4})?$/,
        example: "90210"
    }
];

const PHONE_VALIDATION_CONFIG = {
    fallbackRegions: ["GB", "IE", "CA", "US", "FR"] as CountryCode[],
    examples: ["+44 117 2345678", "+1 416 555 0123", "+33 6 12 34 56 78"]
};

const POSTCODE_EXAMPLES_TEXT = POSTCODE_VALIDATION_RULES
    .map((rule) => rule.example)
    .join(", ");
const PHONE_EXAMPLES_TEXT = PHONE_VALIDATION_CONFIG.examples.join(", ");

const matchesAnyPattern = (value: string, rules: PatternValidationRule[]): boolean => {
    return rules.some((rule) => rule.pattern.test(value));
};

const isValidPhoneNumber = (value: string): boolean => {
    const hasCountryCode = value.startsWith("+");
    if (hasCountryCode) {
        return Boolean(parsePhoneNumberFromString(value)?.isValid());
    }

    return PHONE_VALIDATION_CONFIG.fallbackRegions.some((region) =>
        parsePhoneNumberFromString(value, region)?.isValid()
    );
};

const REGEX_HOLDER = {
    NAME: new RegExp(/^[A-Za-z0-9 .()-<!#$%&'*+/=?^_`{|}~-]+$/),
    LOGIN: new RegExp(/^[A-Za-z0-9.\-_]+$/),
    // eslint-disable-next-line no-control-regex
    EMAIL: new RegExp("(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|\"(?:[\\x01-\\x08\\x0b\\x0c\\x0e-\\x1f\\x21\\x23-\\x5b\\x5d-\\x7f]|\\\\[\\x01-\\x09\\x0b\\x0c\\x0e-\\x7f])*\")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\\x01-\\x08\\x0b\\x0c\\x0e-\\x1f\\x21-\\x5a\\x53-\\x7f]|\\\\[\\x01-\\x09\\x0b\\x0c\\x0e-\\x7f])+)])"),
    INTEGER_NUMBER: new RegExp(/^(0|[1-9]\d*)$/),
    FLOAT_NUMBER: new RegExp(/^\d+(\.\d+)$/),
    CUSTOMER_KEY: new RegExp(/^[A-Za-z0-9 .,\-_]+$/),
    CONDITION_DATE: new RegExp(/^(0[1-9]|1[012])\/(0[1-9]|[12][0-9]|3[01])\/(19|20)\d\d$/),
    HEX_COLOR: new RegExp(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/),
    URL: new RegExp(/^https?:\/\/(www\.)?[A-Za-z0-9@:%_+\-~#=][-A-Za-z0-9@:%_+~#=.]{0,255}\.[A-Za-z0-9()]{1,6}\b([-A-Za-z0-9()@:%_+.~#?&/=]*)$/),
    POSITIVE_NUMBER: new RegExp(/^[1-9]\d*$/),
    STRIPE_PUBLIC_KEY: new RegExp(/^pk_(test|live)_[a-zA-Z0-9]{24,}$/),
    STRIPE_SECRET_KEY: new RegExp(/^sk_(test|live)_[a-zA-Z0-9]{24,}$/),
}

export const ERRORS_HOLDER = {
    VALID: {
        valid: true
    },
    FILE_PATH_INCORRECT_SART: {
        valid: false,
        message: "The path to the file on the SFTP server must start with ‘/’"
    },
    FILE_UNSUPPORTED_EXTENSION: {
        valid: false,
        message: "You can only use file with .csv extension with selected vehicle data provider"
    },
    EMPTY_FIELD: (fieldName: string) => ({
        valid: false,
        message: `${fieldName} field can not be empty`
    }),
    EMPTY_LIST: (fieldName: string) => ({
        valid: false,
        message: `${fieldName} field list can not be empty`
    }),
    NOT_THE_SAME_DATA_PROVIDER_FOR_ASSISTANT: {
        valid: false,
        message: 'All selected Franchises must have Locations with NO_PROVIDER or the same data provider'
    },
    NOT_THE_SAME_DATA_PROVIDER_FOR_FRANCHISE: {
        valid: false,
        message: 'All selected locations must use NO_PROVIDER or the same data provider'
    },
    NO_PROVIDER_ONLY_NON_STARTER_UNSUPPORTED: {
        valid: false,
        message: 'Only Aime Starter can use all NO_PROVIDER-only locations'
    },
    PIPELINE_MISSING_HASHES: {
        valid: false,
        message: "Location must have either group or location hash configured"
    },
    PIPELINE_DUPLICATE_EMPTY_LOCATION_HASH_GROUP: (groupHash?: string) => ({
        valid: false,
        message: "Assistant cannot contain multiple locations with empty location hash and the same group hash: "
            + abbreviateHash(groupHash)
    }),
    PIPELINE_DUPLICATE_LOCATION_HASH: (locationHash?: string) => ({
        valid: false,
        message: "Assistant cannot contain multiple locations with the same location hash: "
            + abbreviateHash(locationHash)
    }),
    RESERVATION_PIPELINE_UNSUPPORTED: {
        valid: false,
        message: "Aime Reservation is currently not supported for Keyloop data provider"
    },
    PIPELINE_SEARCH_URL_PROCESSOR_MATCH_FIELD_UNSUPPORTED: {
        valid: false,
        message: "For Keyloop Pipeline Search, URL processor field must be KEYLOOP_VEHICLE_ID"
    },
    PIPELINE_STOCK_FILE_URL_PROCESSOR_MATCH_FIELD_UNSUPPORTED: {
        valid: false,
        message: "For Pipeline Stock File, URL processor field must be PIPELINE_STOCK_FILE_VEHICLE_ID"
    },
    PIPELINE_URL_PROCESSOR_MATCH_TYPE_UNSUPPORTED: {
        valid: false,
        message: "For Keyloop Pipeline Search or Pipeline Stock File, URL processor match type must be EXACT_MATCH"
    },
    NON_PIPELINE_KEYLOOP_VEHICLE_ID_UNSUPPORTED: {
        valid: false,
        message: "KEYLOOP_VEHICLE_ID is only supported for Keyloop Pipeline Search locations"
    },
    NON_PIPELINE_STOCK_FILE_VEHICLE_ID_UNSUPPORTED: {
        valid: false,
        message: "PIPELINE_STOCK_FILE_VEHICLE_ID is only supported for Pipeline Stock File locations"
    },
    URL_PROCESSOR_NO_PROVIDER_UNSUPPORTED: {
        valid: false,
        message: "URL processor is only supported when the assistant has at least one used vehicle data provider"
    },
    INVALID_FIELD: (fieldName: string) => ({
        valid: false,
        message: `${fieldName} field is invalid`
    }),
    TOO_MUCH_CHAR: (fieldName: string, maxLength: number) => ({
        valid: false,
        message: `Too long ${fieldName}, max length is ${maxLength} characters`
    }),
    INVALID_COLOR: (fieldName: string) => ({
        valid: false,
        message: `${fieldName} must be a valid hex color (e.g., #FF0000)`
    }),
    INVALID_URL: (fieldName: string) => ({
        valid: false,
        message: `${fieldName} must be a valid URL (e.g., https://example.com)`
    }),
    INVALID_EMAIL: (fieldName: string) => ({
        valid: false,
        message: `${fieldName} must be a valid email address (e.g., example@domain.com)`
    }),
    INVALID_POSITIVE_NUMBER: (fieldName: string) => ({
        valid: false,
        message: `${fieldName} must be a positive number greater than 0`
    }),
    INVALID_STRIPE_PUBLIC_KEY: (fieldName: string) => ({
        valid: false,
        message: `Stripe public key starting with 'pk_test_' or 'pk_live_'`
    }),
    INVALID_STRIPE_SECRET_KEY: (fieldName: string) => ({
        valid: false,
        message: `Stripe secret key starting with 'sk_test_' or 'sk_live_'`
    }),
    INVALID_POSTCODE: (fieldName: string) => ({
        valid: false,
        message: `Invalid postcode format. Examples: ${POSTCODE_EXAMPLES_TEXT}`
    }),
    INVALID_PHONE_NUMBER: (fieldName: string) => ({
        valid: false,
        message: `Invalid phone number format. Examples: ${PHONE_EXAMPLES_TEXT}`
    }),
    SERVER_ERROR: (fieldName: string) => ({
        valid: false,
        message: `API provider has server error, unable to verify`
    }),
    INVALID_API_KEY: (fieldName: string) => ({
        valid: false,
        message: `Invalid API Key`
    }),
    VALUE_LESS_THAN_0: (fieldName: string) => ({
        valid: false,
        message: `${fieldName} must be greater than 0`
    }),
    VALUE_MORE_THAN_100: (fieldName: string) => ({
        valid: false,
        message: `${fieldName} must be less than 100`
    }),
}

export const FloatNumberValidator: ValueValidator<number> = (fieldName: string, value?: number) => {
    if (value || value === 0) {
        if (REGEX_HOLDER.FLOAT_NUMBER.test(value.toString()) || REGEX_HOLDER.INTEGER_NUMBER.test(value.toString())) {
            return ERRORS_HOLDER.VALID;
        } else {
            return ERRORS_HOLDER.INVALID_FIELD(fieldName);
        }
    } else {
        return ERRORS_HOLDER.EMPTY_FIELD(fieldName);
    }
}

export const PercentageValidator: ValueValidator<number> = (fieldName: string, value?: number) => {
    if (value) {
        if (REGEX_HOLDER.FLOAT_NUMBER.test(value.toString()) || REGEX_HOLDER.INTEGER_NUMBER.test(value.toString())) {
            if (value < 0) {
                return ERRORS_HOLDER.VALUE_LESS_THAN_0(fieldName)
            }

            if (value > 100) {
                return ERRORS_HOLDER.VALUE_MORE_THAN_100(fieldName)
            }

            return ERRORS_HOLDER.VALID
        } else {
            return ERRORS_HOLDER.INVALID_FIELD(fieldName);
        }
    } else {
        return ERRORS_HOLDER.EMPTY_FIELD(fieldName);
    }
}

export const NotNullValidator: ValueValidator<any> = (fieldName: string, value?: any) => {
    if (value == null || value === 'undefined') {
        return ERRORS_HOLDER.EMPTY_FIELD(fieldName);
    } else {
        return ERRORS_HOLDER.VALID;
    }
}

export const NotEmptyStringValidator: ValueValidator<string | undefined> = (fieldName: string, value?: string | undefined) => {
    const clearValue = value?.trim();
    if (clearValue == null || value === 'undefined' || clearValue === '') {
        return ERRORS_HOLDER.EMPTY_FIELD(fieldName);
    } else {
        return ERRORS_HOLDER.VALID;
    }
}

export const FilePathValidator: ValueValidator<string | undefined> = (fieldName: string, value?: string | undefined) => {
    const clearValue = value?.trim();
    if (clearValue == null || value === 'undefined' || clearValue === '') {
        return ERRORS_HOLDER.EMPTY_FIELD(fieldName);
    } else {
        if (clearValue.startsWith("/")) {
            if (clearValue.endsWith(".csv")) {
                return ERRORS_HOLDER.VALID;
            } else {
                return ERRORS_HOLDER.FILE_UNSUPPORTED_EXTENSION;
            }
        } else {
            return ERRORS_HOLDER.FILE_PATH_INCORRECT_SART;
        }
    }
}

export const NotEmptyListValidator: ValueValidator<any[] | undefined> = (fieldName: string, value?: string[] | undefined) => {
    if (value == null || value.length === 0) {
        return ERRORS_HOLDER.EMPTY_LIST(fieldName);
    } else {
        return ERRORS_HOLDER.VALID;
    }
}

const validateSameDataProviderLocations = (
    fieldName: string,
    value: LocationOptionDTO[] | undefined,
    invalidOutcome: ValidatorOutcome
): ValidatorOutcome => {
    if (value == null || value.length === 0) {
        return ERRORS_HOLDER.EMPTY_LIST(fieldName);
    }

    const hasMissingProvider = value.some((location) => location.vehicleDataProvider == null);
    if (hasMissingProvider) {
        return invalidOutcome;
    }

    const nonNoProviderList = value
        .map((location) => location.vehicleDataProvider)
        .filter((provider): provider is VehicleDataProvider =>
            provider != null && provider !== VehicleDataProvider.NoProvider
        );
    const nonNoProviderSet = new Set(nonNoProviderList);
    if (nonNoProviderSet.size > 1) {
        return invalidOutcome;
    }

    return ERRORS_HOLDER.VALID;
}

const validateBotTypeSupportsSelectedProviders = (
    botType: BotType | undefined,
    locations: LocationOptionDTO[] | undefined
): ValidatorOutcome => {
    if (!botType || !locations || locations.length === 0) {
        return ERRORS_HOLDER.VALID;
    }

    const hasMissingProvider = locations.some((location) => location.vehicleDataProvider == null);
    if (hasMissingProvider) {
        return ERRORS_HOLDER.VALID;
    }

    const hasRealProvider = locations.some((location) =>
        location.vehicleDataProvider != null && location.vehicleDataProvider !== VehicleDataProvider.NoProvider
    );
    if (!hasRealProvider && botType !== BotType.AimeStarter) {
        return ERRORS_HOLDER.NO_PROVIDER_ONLY_NON_STARTER_UNSUPPORTED;
    }

    return ERRORS_HOLDER.VALID;
}

export const SameDataProviderLocationsValidator: ValueValidator<LocationOptionDTO[]> = (fieldName: string, value?: LocationOptionDTO[]) =>
    validateSameDataProviderLocations(fieldName, value, ERRORS_HOLDER.NOT_THE_SAME_DATA_PROVIDER_FOR_FRANCHISE);

const validateLocationSelectionProviderRules = (fieldName: string, value?: LocationOptionDTO[]): ValidatorOutcome => {
    const sameProviderValidationResult = validateSameDataProviderLocations(
        fieldName,
        value,
        ERRORS_HOLDER.NOT_THE_SAME_DATA_PROVIDER_FOR_ASSISTANT
    );
    if (!sameProviderValidationResult.valid || !value || value.length === 0) {
        return sameProviderValidationResult;
    }

    return ERRORS_HOLDER.VALID;
}

export const SelectedLocationsValidator = (
    fieldName: string,
    value?: LocationOptionDTO[],
    assistantGroupHash?: string
): ValidatorOutcome => {
    const baseValidationResult = validateLocationSelectionProviderRules(fieldName, value);
    if (!baseValidationResult.valid || !value || value.length === 0) {
        return baseValidationResult;
    }

    return validatePipelineSearchLocationHashes(value, assistantGroupHash);
}

export const validateBotTypeForSelectedLocations = (
    botType: BotType | undefined,
    locations: LocationOptionDTO[] | undefined
): ValidatorOutcome => validateBotTypeSupportsSelectedProviders(botType, locations);

const normalizeHash = (value?: string): string | undefined => {
    const normalized = (value || "").trim();
    return normalized || undefined;
}

const validatePipelineSearchLocationHashes = (
    value: LocationOptionDTO[],
    assistantGroupHash?: string
): ValidatorOutcome => {
    if (!isAssistantPipelineSearchProvider(value)) {
        return ERRORS_HOLDER.VALID;
    }

    const pipelineSearchLocations = getAssistantLocationsByVehicleDataProvider(
        value,
        VehicleDataProvider.KeyloopPipelineSearch
    );
    const usedLocationHashes = new Set<string>();
    const usedGroupHashesForBlankLocationHash = new Set<string>();
    const groupHash = normalizeHash(assistantGroupHash);

    for (const location of pipelineSearchLocations) {
        const locationHash = normalizeHash(location.locationHash);

        if (!locationHash && !groupHash) {
            return ERRORS_HOLDER.PIPELINE_MISSING_HASHES;
        }

        if (!locationHash) {
            if (usedGroupHashesForBlankLocationHash.has(groupHash as string)) {
                return ERRORS_HOLDER.PIPELINE_DUPLICATE_EMPTY_LOCATION_HASH_GROUP(groupHash);
            }
            usedGroupHashesForBlankLocationHash.add(groupHash as string);
            continue;
        }

        if (usedLocationHashes.has(locationHash)) {
            return ERRORS_HOLDER.PIPELINE_DUPLICATE_LOCATION_HASH(locationHash);
        }
        usedLocationHashes.add(locationHash);
    }

    return ERRORS_HOLDER.VALID;
}

export const validateUrlProcessorMatchField = (
    matchField: MatchField | undefined,
    assistantVehicleDataProvider: VehicleDataProvider | undefined,
    assistantHasVehicleDataProvider: boolean
): ValidatorOutcome => {
    if (!assistantHasVehicleDataProvider) {
        return ERRORS_HOLDER.URL_PROCESSOR_NO_PROVIDER_UNSUPPORTED;
    }

    if (assistantVehicleDataProvider === VehicleDataProvider.KeyloopPipelineSearch
        && matchField !== MatchField.KeyloopVehicleId) {
        return ERRORS_HOLDER.PIPELINE_SEARCH_URL_PROCESSOR_MATCH_FIELD_UNSUPPORTED;
    }

    if (assistantVehicleDataProvider === VehicleDataProvider.PipelineStockFile
        && matchField !== MatchField.PipelineStockFileVehicleId) {
        return ERRORS_HOLDER.PIPELINE_STOCK_FILE_URL_PROCESSOR_MATCH_FIELD_UNSUPPORTED;
    }

    if (assistantVehicleDataProvider !== VehicleDataProvider.KeyloopPipelineSearch
        && matchField === MatchField.KeyloopVehicleId) {
        return ERRORS_HOLDER.NON_PIPELINE_KEYLOOP_VEHICLE_ID_UNSUPPORTED;
    }

    if (assistantVehicleDataProvider !== VehicleDataProvider.PipelineStockFile
        && matchField === MatchField.PipelineStockFileVehicleId) {
        return ERRORS_HOLDER.NON_PIPELINE_STOCK_FILE_VEHICLE_ID_UNSUPPORTED;
    }

    return ERRORS_HOLDER.VALID;
}

export const validateUrlProcessorMatchType = (
    matchType: MatchType | undefined,
    assistantVehicleDataProvider: VehicleDataProvider | undefined,
    assistantHasVehicleDataProvider: boolean
): ValidatorOutcome => {
    if (!assistantHasVehicleDataProvider) {
        return ERRORS_HOLDER.URL_PROCESSOR_NO_PROVIDER_UNSUPPORTED;
    }

    if ((assistantVehicleDataProvider === VehicleDataProvider.KeyloopPipelineSearch
            || assistantVehicleDataProvider === VehicleDataProvider.PipelineStockFile) &&
        matchType !== MatchType.ExactMatch) {
        return ERRORS_HOLDER.PIPELINE_URL_PROCESSOR_MATCH_TYPE_UNSUPPORTED;
    }

    return ERRORS_HOLDER.VALID;
}

const abbreviateHash = (value?: string): string => {
    if (!value) {
        return "unknown";
    }

    return value.length <= 5
        ? value
        : value.substring(0, 5) + "...";
}

const validateMaxValue = (i: number, rules: RetailPriceRulesRequest[]): string | undefined => {
    const rule = rules[i]

    if (!rule.max) {
        return "Field can not be empty"
    }

    if (i > 0) {
        const previousRule = rules[i - 1]
        if (previousRule.max && rule.max && rule.max <= previousRule.max) {
            return "Value must be higher that previous one in a chain"
        }
    }
};

export const VehiclePriceDiscountRulesValidator: ValueValidator<RetailPriceRulesRequest[]> = (fieldName: string, values?: RetailPriceRulesRequest[]) => {
    const errorMessages = new Map();

    if (values) {
        for (let i = 0; i < values.length; i++) {
            const maxValueErrorMessage = validateMaxValue(i, values)
            const subtractionErrorMessage = validateSubtractionField(values[i]?.subtraction)

            if (maxValueErrorMessage || subtractionErrorMessage) {
                const errorMessage = {
                    maxValueErrorMessage: maxValueErrorMessage,
                    subtractionErrorMessage: subtractionErrorMessage,
                }
                errorMessages.set(i, errorMessage)
            }
        }

        if (errorMessages.size) {
            return {
                valid: false,
                message: JSON.stringify(Object.fromEntries(errorMessages))
            }
        }
    }
    return ERRORS_HOLDER.VALID;
}

export const validateSubtractionField = (value?: SubtractionRequest) => {
    if (!value) {
        return "Field can not be empty"
    } else {
        if ((!value.subtractionValue && value.subtractionValue !== 0) || value.subtractionValue === -1) {
            return "Field can not be empty"
        }

        if (value.subtractionValue < 0) {
            return "Value must be greater than 0"
        }

        if (value?.subtractionType === SubtractionType.Percentage && value.subtractionValue > 100) {
            return "Value must be less than 100"
        }
    }
}

export const ConditionMultipliersRequestValidator: ValueValidator<ConditionMultipliersRequest> = (fieldName: string, value?: ConditionMultipliersRequest) => {
    const errorMessages = new Map();

    const excellentSubtractionError = validateSubtractionField(value?.excellentSubtraction)
    const goodSubtractionError = validateSubtractionField(value?.goodSubtraction)
    const averageSubtractionError = validateSubtractionField(value?.averageSubtraction)
    const belowAverageSubtractionError = validateSubtractionField(value?.belowAverageSubtraction)
    const poorSubtractionError = validateSubtractionField(value?.poorSubtraction)

    if (excellentSubtractionError ||
        goodSubtractionError ||
        averageSubtractionError ||
        belowAverageSubtractionError ||
        poorSubtractionError) {

        errorMessages.set("excellentSubtraction", excellentSubtractionError)
        errorMessages.set("goodSubtraction", goodSubtractionError)
        errorMessages.set("averageSubtraction", averageSubtractionError)
        errorMessages.set("belowAverageSubtraction", belowAverageSubtractionError)
        errorMessages.set("poorSubtraction", poorSubtractionError)

        return {
            valid: false,
            message: JSON.stringify(Object.fromEntries(errorMessages))
        }
    } else {
        return ERRORS_HOLDER.VALID;
    }
}

export const ServiceHistoryMultipliersRequestValidator: ValueValidator<ServiceHistoryMultipliersRequest> = (fieldName: string, value?: ServiceHistoryMultipliersRequest) => {
    const errorMessages = new Map();

    const fullHistorySubtractionError = validateSubtractionField(value?.fullHistorySubtraction)
    const partHistorySubtractionError = validateSubtractionField(value?.partHistorySubtraction)
    const noHistorySubtractionError = validateSubtractionField(value?.noHistorySubtraction)

    if (fullHistorySubtractionError ||
        partHistorySubtractionError ||
        noHistorySubtractionError) {

        errorMessages.set("fullHistorySubtraction", fullHistorySubtractionError)
        errorMessages.set("partHistorySubtraction", partHistorySubtractionError)
        errorMessages.set("noHistorySubtraction", noHistorySubtractionError)

        return {
            valid: false,
            message: JSON.stringify(Object.fromEntries(errorMessages))
        }
    } else {
        return ERRORS_HOLDER.VALID;
    }
}

export const ModifierRulesValidator: ValueValidator<UpdateModifierRulesRequest> = (fieldName: string, value?: UpdateModifierRulesRequest) => {
    if (!value?.isActive) {
        return ERRORS_HOLDER.VALID;
    }

    const errorMessages = new Map();

    if (!value.ruleSections || value.ruleSections.size === 0) {
        return ERRORS_HOLDER.VALID;
    }

    value.ruleSections.forEach((section: UpdateRuleSectionRequest) => {
        const sectionErrors = new Map();

        if (section.valuationAction?.actionType === ActionType.IncreasePrice ||
            section.valuationAction?.actionType === ActionType.ReducePrice) {
            const priceModifierError = validateSubtractionField(section.valuationAction.priceModifier);
            if (priceModifierError) {
                sectionErrors.set('priceModifier', priceModifierError);
            }
        }

        if (section.conditionGroups && section.conditionGroups.size > 0) {
            section.conditionGroups.forEach((group: UpdateConditionGroupRequest) => {
                const groupErrors = new Map();
                if (group.conditions && group.conditions.size > 0) {
                    group.conditions.forEach((condition: UpdateConditionRequest) => {
                        const conditionErrors: string[] = [];

                        if (!condition.conditionField) {
                            conditionErrors.push("Field is required");
                        }
                        if (!condition.operator) {
                            conditionErrors.push("Operator is required");
                        }

                        if (conditionErrors.length > 0) {
                            groupErrors.set(`condition_${condition.id}`, conditionErrors.join(", "));
                        }
                    });
                }

                if (groupErrors.size > 0) {
                    sectionErrors.set(`group_${group.id}`, Object.fromEntries(groupErrors));
                }
            });
        }

        if (sectionErrors.size > 0) {
            errorMessages.set(`section_${section.id}`, Object.fromEntries(sectionErrors));
        }
    });

    if (errorMessages.size > 0) {
        return {
            valid: false,
            message: JSON.stringify(Object.fromEntries(errorMessages))
        };
    }

    return ERRORS_HOLDER.VALID;
}


export const NotEmptyValidator: ValueValidator<string | undefined> = (fieldName: string, value?: string) => {
    if (!value) {
        return ERRORS_HOLDER.EMPTY_FIELD(fieldName);
    } else {
        return ERRORS_HOLDER.VALID;
    }
}


export function getNotValueValidator<T extends any>(unacceptableValue: T): ValueValidator<T> {
    return (fieldName, value) => {
        if (value === unacceptableValue) {
            return ERRORS_HOLDER.EMPTY_FIELD(fieldName);
        } else {
            return ERRORS_HOLDER.VALID;
        }
    }
}

export const DefaultValidValidator: ValueValidator<string | number> = (fieldName: string, value?: string | number) => {
    return ERRORS_HOLDER.VALID;
}

export const booleanValidator: ValueValidator<boolean | undefined> = (fieldName: string, value?: boolean) => {
    if (value === undefined) {
        return ERRORS_HOLDER.EMPTY_FIELD(fieldName);
    } else {
        return ERRORS_HOLDER.VALID;
    }
}

export const NameValidator: ValueValidator<string> = (fieldName: string, value?: string) => {
    if (value) {
        if (REGEX_HOLDER.NAME.test(value)) {
            return ERRORS_HOLDER.VALID;
        } else {
            return ERRORS_HOLDER.INVALID_FIELD(fieldName);
        }
    } else {
        return ERRORS_HOLDER.EMPTY_FIELD(fieldName);
    }
}

export const OptionalEmailValidator: ValueValidator<string> = (fieldName: string, value?: string) => {
    if (value == null || value.length === 0) {
        return ERRORS_HOLDER.VALID;
    } else {
        if (REGEX_HOLDER.EMAIL.test(value)) {
            return ERRORS_HOLDER.VALID;
        } else {
            return ERRORS_HOLDER.INVALID_EMAIL(fieldName);
        }
    }
}


const validateMultipleEmails = (fieldName: string, value?: string, required = false) => {
    if (!value || value.trim().length === 0) {
        return required ? ERRORS_HOLDER.EMPTY_FIELD(fieldName) : ERRORS_HOLDER.VALID;
    }

    const emails = value.split(';').map(email => email.trim());

    if (emails.some(email => email.length === 0)) {
        return {
            valid: false,
            message: `${fieldName} contains empty email addresses`
        };
    }

    if (emails.some(email => email.includes(' '))) {
        return {
            valid: false,
            message: `${fieldName} contains invalid formatting`
        };
    }

    const invalidEmails = emails.filter(email => !REGEX_HOLDER.EMAIL.test(email));

    if (invalidEmails.length > 0) {
        return {
            valid: false,
            message: `${fieldName} contains invalid email addresses: ${invalidEmails.join(', ')}`
        };
    }

    return ERRORS_HOLDER.VALID;
}

export const MultipleEmailNotEmptyValidator: ValueValidator<string> = (fieldName, value) => {
    return validateMultipleEmails(fieldName, value, true);
}

export const MultipleEmailValidator: ValueValidator<string> = (fieldName, value) => {
    return validateMultipleEmails(fieldName, value, false);
}

export const IntegerNumberValidator: ValueValidator<number> = (fieldName: string, value?: number) => {
    if (value === undefined || value === null || Number.isNaN(value)) {
        return ERRORS_HOLDER.EMPTY_FIELD(fieldName);
    }

    const strValue = String(value);
    if (REGEX_HOLDER.INTEGER_NUMBER.test(strValue)) {
        return ERRORS_HOLDER.VALID;
    } else {
        return ERRORS_HOLDER.INVALID_FIELD(fieldName);
    }
}

export const PositiveIntegerNumberValidator: ValueValidator<number> = (fieldName: string, value?: number) => {
    if (value === undefined || value === null || Number.isNaN(value)) {
        return ERRORS_HOLDER.EMPTY_FIELD(fieldName);
    }

    const strValue = String(value);
    if (REGEX_HOLDER.INTEGER_NUMBER.test(strValue) && strValue !== '0') {
        return ERRORS_HOLDER.VALID;
    } else {
        return ERRORS_HOLDER.INVALID_FIELD(fieldName);
    }
}

export const SURVEY_MESSAGE_COUNT_MAX = 50;

export const SurveyMessageCountValidator: ValueValidator<number> = (fieldName: string, value?: number) => {
    if (value === undefined || value === null || Number.isNaN(value)) {
        return ERRORS_HOLDER.EMPTY_FIELD(fieldName);
    }

    const strValue = String(value);
    if (REGEX_HOLDER.POSITIVE_NUMBER.test(strValue) && value <= SURVEY_MESSAGE_COUNT_MAX) {
        return ERRORS_HOLDER.VALID;
    }
    return {
        valid: false,
        message: `${fieldName} must be a whole number from 1 to ${SURVEY_MESSAGE_COUNT_MAX}`
    };
}

// Match the widget_appearance_settings column sizes and the platform's save validation.
export const ASSISTANT_NAME_MAX_LENGTH = 50;
export const PERSONA_PROMPT_MAX_LENGTH = 1000;
export const TERMS_TITLE_MAX_LENGTH = 255;

const requiredTextWithin = (maxLength: number): ValueValidator<string | undefined> => (fieldName: string, value?: string) => {
    if (!value?.trim()) {
        return ERRORS_HOLDER.EMPTY_FIELD(fieldName);
    }
    if (value.trim().length > maxLength) {
        return {
            valid: false,
            message: `${fieldName} must be at most ${maxLength} characters`
        };
    }
    return ERRORS_HOLDER.VALID;
}

export const AssistantNameValidator = requiredTextWithin(ASSISTANT_NAME_MAX_LENGTH);

export const PersonaPromptValidator = requiredTextWithin(PERSONA_PROMPT_MAX_LENGTH);

export const TermsTitleValidator = requiredTextWithin(TERMS_TITLE_MAX_LENGTH);

export const HexColorValidator: ValueValidator<string> = (fieldName: string, value?: string) => {
    if (!value) {
        return ERRORS_HOLDER.EMPTY_FIELD(fieldName);
    }

    if (!REGEX_HOLDER.HEX_COLOR.test(value)) {
        return ERRORS_HOLDER.INVALID_COLOR(fieldName);
    }

    return ERRORS_HOLDER.VALID;
}

export const UrlValidator: ValueValidator<string> = (fieldName: string, value?: string) => {
    if (!value || value.length === 0) {
        return ERRORS_HOLDER.EMPTY_FIELD(fieldName);
    } else {
        if (REGEX_HOLDER.URL.test(value)) {
            return ERRORS_HOLDER.VALID;
        } else {
            return ERRORS_HOLDER.INVALID_URL(fieldName);
        }
    }
}

export const OptionalUrlValidator: ValueValidator<string> = (fieldName: string, value?: string) => {
    if (!value || value.length === 0) {
        return ERRORS_HOLDER.VALID;
    } else {
        if (REGEX_HOLDER.URL.test(value)) {
            return ERRORS_HOLDER.VALID;
        } else {
            return ERRORS_HOLDER.INVALID_URL(fieldName);
        }
    }
}

export const OptionalPositiveNumberValidator: ValueValidator<number> = (fieldName: string, value?: number) => {
    const strValue = String(value);
    if (strValue == null || strValue.length === 0 || value == null) {
        return ERRORS_HOLDER.VALID;
    } else if (!REGEX_HOLDER.POSITIVE_NUMBER.test(strValue)) {
        return ERRORS_HOLDER.INVALID_POSITIVE_NUMBER(fieldName);
    }
    return ERRORS_HOLDER.VALID;
}

export const PositiveNumberValidator: ValueValidator<number> = (fieldName: string, value?: number) => {
    if (value === undefined || value === null || Number.isNaN(value)) {
        return ERRORS_HOLDER.EMPTY_FIELD(fieldName);
    }
    const strValue = String(value);
    if (!REGEX_HOLDER.POSITIVE_NUMBER.test(strValue)) {
        return ERRORS_HOLDER.INVALID_POSITIVE_NUMBER(fieldName);
    }
    return ERRORS_HOLDER.VALID;
}

export const StripePublicKeyValidator: ValueValidator<string> = (fieldName: string, value?: string) => {
    if (!value || value.length === 0) {
        return ERRORS_HOLDER.EMPTY_FIELD(fieldName);
    } else {
        if (REGEX_HOLDER.STRIPE_PUBLIC_KEY.test(value)) {
            return ERRORS_HOLDER.VALID;
        } else {
            return ERRORS_HOLDER.INVALID_STRIPE_PUBLIC_KEY(fieldName);
        }
    }
}

export const StripeSecretKeyValidator: ValueValidator<string> = (fieldName: string, value?: string) => {
    if (!value || value.length === 0) {
        return ERRORS_HOLDER.EMPTY_FIELD(fieldName);
    } else {
        if (REGEX_HOLDER.STRIPE_SECRET_KEY.test(value)) {
            return ERRORS_HOLDER.VALID;
        } else {
            return ERRORS_HOLDER.INVALID_STRIPE_SECRET_KEY(fieldName);
        }
    }
}

export const PostcodeValidator: ValueValidator<string> = (fieldName: string, value?: string) => {
    if (!value || value.length === 0) {
        return ERRORS_HOLDER.EMPTY_FIELD(fieldName);
    } else {
        if (matchesAnyPattern(value, POSTCODE_VALIDATION_RULES)) {
            return ERRORS_HOLDER.VALID;
        } else {
            return ERRORS_HOLDER.INVALID_POSTCODE(fieldName);
        }
    }
}

export const UkPostcodeValidator = PostcodeValidator;

export const PhoneNumberValidator: ValueValidator<string> = (fieldName: string, value?: string) => {
    if (!value || value.length === 0) {
        return ERRORS_HOLDER.EMPTY_FIELD(fieldName);
    } else {
        const clearValue = value.trim();

        if (isValidPhoneNumber(clearValue)) {
            return ERRORS_HOLDER.VALID;
        } else {
            return ERRORS_HOLDER.INVALID_PHONE_NUMBER(fieldName);
        }
    }
}

export const DealerIdValidator: ValueValidator<string> = (fieldName: string, value?: string) => {
    if (!value || value.length === 0) {
        return ERRORS_HOLDER.EMPTY_FIELD(fieldName);
    } else {
        if (REGEX_HOLDER.POSITIVE_NUMBER.test(value)) {
            return ERRORS_HOLDER.VALID;
        } else {
            return ERRORS_HOLDER.INVALID_POSITIVE_NUMBER(fieldName);
        }
    }
}

export const SuperchatApiKeyValidator: ValueValidator<string> = async (fieldName, value) => {
    const clearValue = value?.trim();
    if (!clearValue || clearValue === 'undefined') {
        return ERRORS_HOLDER.EMPTY_FIELD(fieldName);
    }

    try {
        const headers = new Headers({
            "Content-Type": "application/json",
            "X-API-KEY": clearValue,
        });

        const res = await fetch("https://api.superchat.com/v1.0/webhooks?limit=50", {headers});

        if (res.ok) return ERRORS_HOLDER.VALID;

        if (res.status === 401 || res.status === 403) {
            return ERRORS_HOLDER.INVALID_API_KEY(fieldName);
        }

        return ERRORS_HOLDER.SERVER_ERROR(fieldName) ?? ERRORS_HOLDER.INVALID_API_KEY(fieldName);
    } catch {
        return ERRORS_HOLDER.INVALID_API_KEY(fieldName);
    }
};

export const RegexPatternValidator: ValueValidator<string> = (fieldName: string, value?: string) => {
    if (!value || value.trim().length === 0) {
        return ERRORS_HOLDER.EMPTY_FIELD(fieldName);
    }

    try {
        new RegExp(value);
        return ERRORS_HOLDER.VALID;
    } catch (error) {
        return {
            valid: false,
            message: `Invalid regex pattern: ${error instanceof Error ? error.message : 'Syntax error'}`
        };
    }
};
